import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withStyleSheet } from 'hoc/styles';
import { Div } from 'components/Div';
import { DatePicker } from 'components/DatePicker';
import { Text } from 'components/Text';
import { Icon } from 'components/Icon';
import { Tooltip } from 'components/Tooltip';
import { EquityAutocomplete } from 'components/EquityAutocomplete';
import { WatchlistAutocomplete } from 'components/WatchlistAutocomplete';
import { SectorAutocomplete } from 'components/SectorAutocomplete';
import { IndexAutocomplete } from 'components/IndexAutocomplete';
import { CAPABILITIES } from 'consts';
import { Dropdown } from 'components/Dropdown';
import { OPERATORS } from 'consts/filters';
import { styleSheet } from './stylesheet';

class DashboardFilterForm extends PureComponent {
    static propTypes = {
        capabilities: PropTypes.arrayOf(PropTypes.string).isRequired,
        changeView: PropTypes.func.isRequired,
        dateRange: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])),
        hasFilters: PropTypes.bool.isRequired,
        hideLabel: PropTypes.bool.isRequired,
        onChange: PropTypes.func.isRequired,
        onShowMenu: PropTypes.func.isRequired,
        passedStyles: PropTypes.objectOf(PropTypes.any),
        styles: PropTypes.objectOf(PropTypes.any).isRequired,
        theme: PropTypes.objectOf(PropTypes.any).isRequired,
        view: PropTypes.string.isRequired,
        operatorOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.string.isRequired
            })
        ).isRequired,
        onChangeOperator: PropTypes.func.isRequired,
        operator: PropTypes.string.isRequired
    };

    static defaultProps = {
        dateRange: undefined,
        passedStyles: {}
    };

    constructor(props) {
        super(props);

        this.renderFilterMenu = this.renderFilterMenu.bind(this);
        this.renderDatePicker = this.renderDatePicker.bind(this);
        this.renderEquityAutocomplete = this.renderEquityAutocomplete.bind(this);
        this.renderSectorAutocomplete = this.renderSectorAutocomplete.bind(this);
        this.renderWatchlistAutocomplete = this.renderWatchlistAutocomplete.bind(this);
    }

    renderButtonText() {
        const { capabilities } = this.props;
        let hasDateFilter = false;
        let text = 'Filter by';
        if (capabilities.includes(CAPABILITIES.dateFilter)) {
            hasDateFilter = true;
            text = 'Filter by Date';
        }
        if (capabilities.includes(CAPABILITIES.equityFilter)) {
            text = `${text}${hasDateFilter ? ', ' : ' '} Company`;
        }
        return <Text>{text}</Text>;
    }

    renderDatePicker() {
        const { styles, onChange, changeView, theme, capabilities } = this.props;

        return (
            <Div styles={styles.dateRangeWrapper}>
                <Div styles={styles.menuTitle}>
                    {capabilities.includes(CAPABILITIES.equityFilter) && (
                        <Div onClick={() => changeView('menu')} styles={styles.closeAutocomplete}>
                            <Icon type="arrowLeft" color={theme.colors.gray04} />
                            <Text size={0} weight="medium" uppercase>
                                change type
                            </Text>
                        </Div>
                    )}
                    <Text size={0} weight="medium" uppercase>
                        date range
                    </Text>
                </Div>
                <DatePicker name="dashDateRange" range onChange={onChange} styles={styles.dateRange} />
            </Div>
        );
    }

    renderEquityAutocomplete() {
        const { styles, theme, changeView, onChange } = this.props;

        return (
            <Div styles={styles.autocompleteMenu}>
                <Div styles={styles.menuTitle}>
                    <Div onClick={() => changeView('menu')} styles={styles.closeAutocomplete}>
                        <Icon type="arrowLeft" color={theme.colors.gray04} />
                        <Text size={0} weight="medium" uppercase>
                            change type
                        </Text>
                    </Div>
                    <Text size={0} weight="medium" uppercase>
                        company
                    </Text>
                </Div>
                <EquityAutocomplete
                    name="equity"
                    onChange={onChange}
                    styles={styles.autocomplete}
                    placeholder="Ticker or Name..."
                    autoFocus
                />
            </Div>
        );
    }

    renderSectorAutocomplete() {
        const { styles, theme, changeView, onChange, operatorOptions, onChangeOperator, operator } = this.props;

        return (
            <Div styles={styles.autocompleteMenu}>
                <Div styles={styles.menuTitle}>
                    <Div onClick={() => changeView('menu')} styles={styles.closeAutocomplete}>
                        <Icon type="arrowLeft" color={theme.colors.gray04} />
                        <Text size={0} weight="medium" uppercase>
                            change type
                        </Text>
                    </Div>
                    <Text size={0} weight="medium" uppercase>
                        sector
                    </Text>
                </Div>
                <Dropdown
                    initialLabel={operator}
                    options={operatorOptions}
                    value={operator}
                    onChange={onChangeOperator}
                    disableHoverOpen
                    name="sector"
                    placeholder="Operator"
                    styles={styles.dropdownContainer}
                />
                <SectorAutocomplete
                    placeholder="Select sector..."
                    name="sector"
                    styles={styles.autocomplete}
                    onChange={onChange}
                    autoFocus
                />
            </Div>
        );
    }

    renderIndexAutocomplete() {
        const { styles, theme, changeView, onChange } = this.props;

        return (
            <Div styles={styles.autocompleteMenu}>
                <Div styles={styles.menuTitle}>
                    <Div onClick={() => changeView('menu')} styles={styles.closeAutocomplete}>
                        <Icon type="arrowLeft" color={theme.colors.gray04} />
                        <Text size={0} weight="medium" uppercase>
                            change type
                        </Text>
                    </Div>
                    <Text size={0} weight="medium" uppercase>
                        index
                    </Text>
                </Div>
                <IndexAutocomplete
                    placeholder="Select index..."
                    name="index"
                    styles={styles.autocomplete}
                    onChange={onChange}
                    autoFocus
                />
            </Div>
        );
    }

    renderWatchlistAutocomplete() {
        const { styles, theme, changeView, onChange } = this.props;

        return (
            <Div styles={styles.autocompleteMenu}>
                <Div styles={styles.menuTitle}>
                    <Div onClick={() => changeView('menu')} styles={styles.closeAutocomplete}>
                        <Icon type="arrowLeft" color={theme.colors.gray04} />
                        <Text size={0} weight="medium" uppercase>
                            change type
                        </Text>
                    </Div>
                    <Text size={0} weight="medium" uppercase>
                        by watchlist
                    </Text>
                </Div>
                <WatchlistAutocomplete
                    placeholder="Select watchlist..."
                    name="watchlist"
                    styles={styles.autocomplete}
                    onChange={onChange}
                    autoFocus
                />
            </Div>
        );
    }

    renderFilterMenu() {
        const { styles, theme, capabilities, changeView, view } = this.props;
        if (view === 'equity') {
            return this.renderEquityAutocomplete();
        }

        if (view === 'sector') {
            return this.renderSectorAutocomplete();
        }

        if (view === 'watchlist') {
            return this.renderWatchlistAutocomplete();
        }

        if (view === 'index') {
            return this.renderIndexAutocomplete();
        }

        if (view === 'daterange' || !capabilities.includes(CAPABILITIES.equityFilter)) {
            return this.renderDatePicker();
        }

        return (
            <Div styles={styles.filterMenu}>
                {capabilities.includes(CAPABILITIES.dateFilter) && (
                    <Div styles={styles.menuOption} onClick={() => changeView('daterange')}>
                        <Div styles={styles.iconContainer}>
                            <Icon type="calendar" color={theme.colors.black01} />
                        </Div>
                        <Text size={3}>Date Range</Text>
                    </Div>
                )}
                {capabilities.includes(CAPABILITIES.equityFilter) && (
                    <Div styles={styles.menuOption} onClick={() => changeView('equity')}>
                        <Div styles={styles.iconContainer}>
                            <Icon type="company" color={theme.colors.black01} />
                        </Div>
                        <Text size={3}>Company</Text>
                    </Div>
                )}
                {capabilities.includes(CAPABILITIES.equityFilter) && (
                    <Div styles={styles.menuOption} onClick={() => changeView('sector')}>
                        <Div styles={styles.iconContainer}>
                            <Icon type="sector" color={theme.colors.black01} />
                        </Div>
                        <Text size={3}>Sector</Text>
                    </Div>
                )}
                {capabilities.includes(CAPABILITIES.equityFilter) && (
                    <Div styles={styles.menuOption} onClick={() => changeView('index')}>
                        <Div styles={styles.iconContainer}>
                            <Icon type="report" color={theme.colors.black01} />
                        </Div>
                        <Text size={3}>Index</Text>
                    </Div>
                )}
                {capabilities.includes(CAPABILITIES.equityFilter) && (
                    <Div styles={styles.menuOption} onClick={() => changeView('watchlist')}>
                        <Div styles={styles.iconContainer}>
                            <Icon type="list02" color={theme.colors.black01} />
                        </Div>
                        <Text size={3}>Watchlist</Text>
                    </Div>
                )}
            </Div>
        );
    }

    render() {
        const { passedStyles, styles, theme, onShowMenu, hasFilters, hideLabel, onChangeOperator } = this.props;
        return (
            <Tooltip
                onShow={onShowMenu}
                isEnabled
                useElementOffsetBottom
                useElementOffsetLeft
                persistOnMouseExit
                hideOnScroll={false}
                useOutsideClickHandler
                xOffset={0}
                yOffset={8}
                content={this.renderFilterMenu}
                onHide={() => onChangeOperator({ value: OPERATORS.is })}
            >
                {({ showTooltip }) => (
                    <Div
                        styles={{ ...styles.container, ...passedStyles }}
                        onClick={showTooltip}
                        className="dashFilterFormButton"
                    >
                        <Icon type="filters" color={theme.colors.white01} />
                        {!hasFilters && !hideLabel && this.renderButtonText()}
                    </Div>
                )}
            </Tooltip>
        );
    }
}

export const DashboardFilterFormUI = compose(withStyleSheet(styleSheet))(DashboardFilterForm);
